import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, TrendingUp, Target, Brain, Shield, ShieldCheck,
  Award, AlertTriangle, Zap, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import DevelopmentTimeline from '@/components/DevelopmentTimeline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const skillLabels = {
  ball_control: 'Ball Control', passing: 'Passing', dribbling: 'Dribbling', finishing: 'Finishing',
  speed_agility: 'Speed & Agility', balance_coordination: 'Balance & Coordination',
  decision_making: 'Decision Making', game_awareness: 'Game Awareness'
};

const levelColors = {
  focus: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'FOCUS', color: '#ef4444' },
  developing: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', label: 'DEVELOPING', color: '#f59e0b' },
  proficient: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'PROFICIENT', color: '#10b981' }
};

const ClubPlayerDetail = () => {
  const { club, filters } = useOutletContext();
  const { playerId } = useParams();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const canvasRef = useRef(null);

  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    if (club?.id && playerId) fetchPlayer();
  }, [club, playerId]);

  useEffect(() => {
    if (player?.radar_data) drawRadar();
  }, [player]);

  const fetchPlayer = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const [athletesRes, timelineRes, analysisRes] = await Promise.all([
        axios.get(`${API}/clubs/intelligence/athletes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clubs/intelligence/athlete/${playerId}/timeline`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { history: [] } })),
        axios.get(`${API}/clubs/intelligence/athlete/${playerId}/analysis`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      const found = athletesRes.data.find(a => a.athlete_id === playerId);
      setPlayer(found);
      setTimeline(timelineRes.data.history || []);
      setAnalysis(analysisRes.data);
    } catch (err) {
      console.error('Player fetch error:', err);
    }
    setLoading(false);
    setTimelineLoading(false);
  };

  const drawRadar = () => {
    const canvas = canvasRef.current;
    if (!canvas || !player?.radar_data) return;

    const ctx = canvas.getContext('2d');
    const size = 280;
    canvas.width = size * 2;
    canvas.height = size * 2;
    ctx.scale(2, 2);

    const center = size / 2;
    const radius = size / 2 - 40;
    const skills = Object.keys(skillLabels);
    const n = skills.length;
    const angleStep = (2 * Math.PI) / n;
    const startAngle = -Math.PI / 2;

    ctx.clearRect(0, 0, size, size);

    // Draw grid rings
    [0.25, 0.5, 0.75, 1].forEach(r => {
      ctx.beginPath();
      for (let i = 0; i <= n; i++) {
        const angle = startAngle + i * angleStep;
        const x = center + Math.cos(angle) * radius * r;
        const y = center + Math.sin(angle) * radius * r;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    });

    // Draw axis lines
    skills.forEach((_, i) => {
      const angle = startAngle + i * angleStep;
      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.lineTo(center + Math.cos(angle) * radius, center + Math.sin(angle) * radius);
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    });

    // Draw data polygon
    const values = skills.map(s => parseFloat(player.radar_data[s] || 0) / 10);
    ctx.beginPath();
    values.forEach((v, i) => {
      const angle = startAngle + i * angleStep;
      const x = center + Math.cos(angle) * radius * v;
      const y = center + Math.sin(angle) * radius * v;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = 'rgba(212, 175, 55, 0.12)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(212, 175, 55, 0.7)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Draw dots and labels
    values.forEach((v, i) => {
      const angle = startAngle + i * angleStep;
      const x = center + Math.cos(angle) * radius * v;
      const y = center + Math.sin(angle) * radius * v;

      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#d4af37';
      ctx.fill();

      // Labels
      const labelRadius = radius + 18;
      const lx = center + Math.cos(angle) * labelRadius;
      const ly = center + Math.sin(angle) * labelRadius;
      ctx.font = '8px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const shortLabels = {
        ball_control: 'Ball Ctrl', passing: 'Pass', dribbling: 'Dribble', finishing: 'Finish',
        speed_agility: 'Speed', balance_coordination: 'Balance', decision_making: 'Decision', game_awareness: 'Awareness'
      };
      ctx.fillText(shortLabels[skills[i]] || skills[i], lx, ly);
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!player) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Player not found</p>
      <Link to={`/club/${club?.id}/intelligence`} className="text-primary text-sm hover:underline mt-2 inline-block">Back to Intelligence</Link>
    </div>
  );

  const lc = levelColors[player.level] || levelColors.focus;
  const decisionColor = player.decision === 'Ready for Promotion' ? 'text-emerald-400' : player.decision === 'Maintain Level' ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-6" data-testid="club-player-detail">
      {/* Back Link */}
      <Link to={`/club/${club?.id}/intelligence`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
        <ArrowLeft size={16} /> Back to Intelligence
      </Link>

      {/* Player Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border/40 rounded-sm p-6"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center text-2xl font-heading text-primary shrink-0">
            {player.name?.charAt(0)}
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl uppercase">{player.name}</h1>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
              {player.position && <span>{player.position}</span>}
              {player.age && <span>{player.age} years old</span>}
              <span>{player.team_name}</span>
              {player.player_type && <span className="text-primary">{player.player_type}</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-3">
              <span className={`font-heading text-3xl ${lc.text}`}>{player.overall_score || '—'}</span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm ${lc.bg} ${lc.text} ${lc.border} border`}>
                {lc.label}
              </span>
            </div>
            <div className={`flex items-center gap-1.5 text-sm ${decisionColor}`}>
              {player.decision === 'Ready for Promotion' ? <Award size={14} /> : player.decision === 'Needs Development' ? <AlertTriangle size={14} /> : <Target size={14} />}
              {player.decision}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Score Cards + Radar */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Area Scores */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-card border border-border/40 rounded-sm p-5 space-y-4"
        >
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground">Area Scores</h2>
          <AreaScoreCard label="Technical" score={player.technical_score} icon={Zap} />
          <AreaScoreCard label="Physical" score={player.physical_score} icon={TrendingUp} />
          <AreaScoreCard label="Tactical" score={player.tactical_score} icon={Brain} />
        </motion.div>

        {/* Radar Chart */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-card border border-border/40 rounded-sm p-5 flex flex-col items-center justify-center"
        >
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-2 self-start">Skill Radar</h2>
          {player.radar_data && Object.keys(player.radar_data).length > 0 ? (
            <canvas ref={canvasRef} style={{ width: 280, height: 280 }} />
          ) : (
            <p className="text-sm text-muted-foreground py-10">No radar data available</p>
          )}
        </motion.div>

        {/* AI Insights */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-card border border-border/40 rounded-sm p-5 space-y-5"
        >
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground">AI Insights</h2>
          
          {player.ai_summary && (
            <div className="bg-primary/5 border border-primary/10 rounded-sm p-3">
              <p className="text-sm leading-relaxed">{player.ai_summary}</p>
            </div>
          )}

          {player.strengths?.length > 0 && (
            <div>
              <h3 className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-1.5">
                <Award size={12} /> Strengths
              </h3>
              <ul className="space-y-1.5">
                {player.strengths.map((s, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <ChevronRight size={12} className="text-emerald-400 mt-1 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {player.development_priorities?.length > 0 && (
            <div>
              <h3 className="text-[10px] uppercase tracking-widest text-red-400 mb-2 flex items-center gap-1.5">
                <Target size={12} /> Development Priorities
              </h3>
              <ul className="space-y-1.5">
                {player.development_priorities.map((s, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <ChevronRight size={12} className="text-red-400 mt-1 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!player.ai_summary && !player.strengths?.length && !player.development_priorities?.length && (
            <p className="text-sm text-muted-foreground py-4 text-center">No AI insights available. Run an evaluation first.</p>
          )}
        </motion.div>
      </div>

      {/* Development Timeline */}
      <DevelopmentTimeline history={timeline} loading={timelineLoading} />

      {/* ═══ AI DECISION ENGINE ═══ */}
      {analysis && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Match Contribution */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-card border border-border/40 rounded-sm p-5 space-y-4"
          >
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Target size={12} className="text-primary" /> Match Contribution
              {analysis.position_group && <span className="text-[9px] px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-400 ml-auto">{analysis.position_group}</span>}
            </h2>
            <div className="grid grid-cols-3 gap-3">
              {analysis.position_group !== 'goalkeeper' ? (
                <>
                  <div className="text-center p-3 bg-zinc-900/50 rounded-sm border border-border/20">
                    <p className="font-heading text-2xl text-primary">{analysis.match.total_goals}</p>
                    <p className="text-[10px] text-muted-foreground uppercase mt-1">Goals</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-900/50 rounded-sm border border-border/20">
                    <p className="font-heading text-2xl text-emerald-400">{analysis.match.total_assists}</p>
                    <p className="text-[10px] text-muted-foreground uppercase mt-1">Assists</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center p-3 bg-zinc-900/50 rounded-sm border border-blue-500/20">
                    <p className="font-heading text-2xl text-blue-400">{analysis.match.total_saves || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase mt-1">Saves</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-900/50 rounded-sm border border-border/20">
                    <p className="font-heading text-2xl text-emerald-400">{analysis.match.total_goals || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase mt-1">Clean Sheets</p>
                  </div>
                </>
              )}
              <div className="text-center p-3 bg-zinc-900/50 rounded-sm border border-border/20">
                <p className="font-heading text-2xl text-amber-400">{analysis.match.total_matches}</p>
                <p className="text-[10px] text-muted-foreground uppercase mt-1">Matches</p>
              </div>
            </div>
            <div className="p-3 bg-zinc-900/30 rounded-sm border border-border/10">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-muted-foreground uppercase">Match Impact</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                  analysis.impact.level === 'high' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                  analysis.impact.level === 'medium' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                  analysis.impact.level === 'low' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                  'bg-zinc-500/10 text-zinc-500 border-zinc-500/20'
                }`}>{analysis.impact.level}</span>
              </div>
              <p className="font-heading text-lg">{analysis.impact.score > 0 ? analysis.impact.score : '—'}<span className="text-xs text-muted-foreground">/10</span></p>
            </div>
            {analysis.match.recent_matches?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] text-muted-foreground uppercase">Recent Matches</p>
                {analysis.match.recent_matches.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] p-1.5 bg-zinc-900/20 rounded-sm">
                    <span className={`font-mono ${m.match_score >= 8 ? 'text-emerald-400' : m.match_score >= 6 ? 'text-amber-400' : 'text-red-400'}`}>{m.match_score}</span>
                    <span className="text-muted-foreground truncate flex-1">vs {m.opponent}</span>
                    {m.goals > 0 && <span className="text-primary">{m.goals}G</span>}
                    {m.assists > 0 && <span className="text-emerald-400">{m.assists}A</span>}
                    {m.saves > 0 && <span className="text-blue-400">{m.saves}S</span>}
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Training vs Match Gap */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="bg-card border border-border/40 rounded-sm p-5 space-y-4"
          >
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <TrendingUp size={12} className="text-primary" /> Training vs Match
            </h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-zinc-900/40 rounded-sm">
                <div className="flex-1">
                  <p className="text-[10px] text-muted-foreground uppercase">Training Score</p>
                  <p className="font-heading text-xl text-blue-400">{analysis.training.overall || '—'}</p>
                </div>
                <div className="text-2xl text-zinc-600 font-heading">vs</div>
                <div className="flex-1 text-right">
                  <p className="text-[10px] text-muted-foreground uppercase">Match Avg</p>
                  <p className="font-heading text-xl text-amber-400">{analysis.match.avg_score || '—'}</p>
                </div>
              </div>
              <div className={`p-3 rounded-sm border ${
                analysis.gap.description === 'consistent' ? 'bg-emerald-500/5 border-emerald-500/20' :
                analysis.gap.description === 'training_stronger' ? 'bg-orange-500/5 border-orange-500/20' :
                analysis.gap.description === 'match_stronger' ? 'bg-blue-500/5 border-blue-500/20' :
                'bg-zinc-900/30 border-border/20'
              }`}>
                <p className={`text-[10px] uppercase tracking-wider mb-1 ${
                  analysis.gap.description === 'consistent' ? 'text-emerald-400' :
                  analysis.gap.description === 'training_stronger' ? 'text-orange-400' :
                  analysis.gap.description === 'match_stronger' ? 'text-blue-400' :
                  'text-zinc-500'
                }`}>
                  {analysis.gap.description === 'consistent' ? 'Consistent Performance' :
                   analysis.gap.description === 'training_stronger' ? 'Training > Match' :
                   analysis.gap.description === 'match_stronger' ? 'Match > Training' :
                   'No Comparison Data'}
                </p>
                <p className="text-sm text-muted-foreground">{analysis.gap.details}</p>
              </div>
              {analysis.trend.direction !== 'no_data' && (
                <div className={`p-3 rounded-sm border ${
                  analysis.trend.direction === 'improving' ? 'bg-emerald-500/5 border-emerald-500/20' :
                  analysis.trend.direction === 'declining' ? 'bg-red-500/5 border-red-500/20' :
                  'bg-zinc-900/30 border-border/20'
                }`}>
                  <p className={`text-[10px] uppercase tracking-wider mb-1 ${
                    analysis.trend.direction === 'improving' ? 'text-emerald-400' :
                    analysis.trend.direction === 'declining' ? 'text-red-400' : 'text-amber-400'
                  }`}>Trend: {analysis.trend.direction}</p>
                  <p className="text-sm text-muted-foreground">{analysis.trend.detail}</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Decision Explanation */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="bg-card border border-border/40 rounded-sm p-5 space-y-4"
          >
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Brain size={12} className="text-primary" /> Decision Engine
            </h2>
            <div className={`p-4 rounded-sm border text-center ${
              analysis.decision.result === 'ready_for_promotion' ? 'bg-emerald-500/10 border-emerald-500/30' :
              analysis.decision.result === 'needs_development' ? 'bg-red-500/10 border-red-500/30' :
              analysis.decision.result === 'performance_drop' ? 'bg-red-500/10 border-red-500/30' :
              analysis.decision.result === 'inconsistency' ? 'bg-orange-500/10 border-orange-500/30' :
              analysis.decision.result === 'maintain_level' ? 'bg-amber-500/10 border-amber-500/30' :
              'bg-zinc-900/30 border-border/20'
            }`}>
              <p className={`text-xs uppercase tracking-widest mb-1 ${
                analysis.decision.result === 'ready_for_promotion' ? 'text-emerald-400' :
                analysis.decision.result === 'needs_development' ? 'text-red-400' :
                analysis.decision.result === 'performance_drop' ? 'text-red-500' :
                analysis.decision.result === 'inconsistency' ? 'text-orange-400' :
                analysis.decision.result === 'maintain_level' ? 'text-amber-400' : 'text-zinc-500'
              }`}>
                {analysis.decision.result?.replace(/_/g, ' ')}
              </p>
              <p className="text-[10px] text-muted-foreground">Confidence: {analysis.decision.confidence}</p>
            </div>
            {analysis.decision.reasons?.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] text-muted-foreground uppercase">Reasons</p>
                {analysis.decision.reasons.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-zinc-900/30 rounded-sm">
                    <ChevronRight size={12} className="text-primary mt-0.5 shrink-0" />
                    <p className="text-sm text-muted-foreground">{r}</p>
                  </div>
                ))}
              </div>
            )}
            {analysis.decision.result === 'no_data' && (
              <p className="text-sm text-muted-foreground text-center py-4">Submit evaluations and match reports for AI decisions</p>
            )}
          </motion.div>
        </div>
      )}

      {/* Skill Breakdown */}
      {player.compiled_skills?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="bg-card border border-border/40 rounded-sm p-5"
        >
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Skill Breakdown</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {player.compiled_skills.map(skill => {
              const slc = skill.score >= 8.1 ? levelColors.proficient : skill.score >= 6.1 ? levelColors.developing : levelColors.focus;
              return (
                <div key={skill.skill} className="flex items-center gap-3 p-3 bg-zinc-900/40 rounded-sm border border-border/10">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm">{skill.skill_name || skillLabels[skill.skill] || skill.skill}</span>
                      <span className={`font-mono text-sm font-bold ${slc.text}`}>{skill.score?.toFixed(1)}</span>
                    </div>
                    <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${(skill.score / 10) * 100}%` }}
                        transition={{ duration: 0.6 }}
                        className="h-full rounded-full" style={{ backgroundColor: slc.color }}
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[9px] uppercase ${slc.text}`}>{skill.level}</span>
                      {skill.has_divergence && (
                        <span className="text-[9px] text-amber-400 flex items-center gap-0.5">
                          <AlertTriangle size={8} /> Divergence
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
};

const AreaScoreCard = ({ label, score, icon: Icon }) => {
  const color = score >= 8.1 ? 'text-emerald-400' : score >= 6.1 ? 'text-amber-400' : score >= 4 ? 'text-red-400' : 'text-zinc-500';
  const bgColor = score >= 8.1 ? 'bg-emerald-500/10' : score >= 6.1 ? 'bg-amber-500/10' : score >= 4 ? 'bg-red-500/10' : 'bg-zinc-500/10';
  return (
    <div className="flex items-center gap-3 p-3 bg-zinc-900/40 rounded-sm">
      <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${bgColor}`}>
        <Icon size={16} className={color} />
      </div>
      <div className="flex-1">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className={`font-heading text-xl ${color}`}>{score > 0 ? score : '—'}</p>
      </div>
      <div className="w-16">
        <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${(score / 10) * 100}%` }}
            transition={{ duration: 0.8 }}
            className={`h-full rounded-full ${score >= 8.1 ? 'bg-emerald-500' : score >= 6.1 ? 'bg-amber-500' : 'bg-red-500'}`}
          />
        </div>
      </div>
    </div>
  );
};

export default ClubPlayerDetail;
